import React, { useState } from 'react';
import PasswordIcons, { HideIcon } from './passwordIcons';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';

interface PasswordFieldProps {
  labelText?: string;
  placeholder?: string;
  name: string;
  onChange: ((e: React.ChangeEvent<any>) => void) & ((values: any) => void);
  errors?: { [field: string]: string };
  touched?: { [field: string]: boolean };
  parentDivClass?: string;
  inputFieldClass?: string;
  showStaricInCaseOfRequired?: boolean;
  showErrorOnChange?: boolean;
  showPasswordInfo?: boolean;
  value?: string;
  autoComplete?: string;
}

const passowrdInfo = [
  'Must be at least 8 characters long',
  'Must include both uppercase and lowercase letters',
  'Must contain both letters and numbers',
  'Must include at least one special character (e.g. ! @ # $ % etc.)',
  'Must not contain spaces or quotation marks',
];

const passwordInfoOnConfirmPassword =
  'Your confirmation password must match the original';

const PasswordField: React.FC<PasswordFieldProps> = ({
  labelText,
  name,
  onChange,
  errors,
  touched,
  parentDivClass,
  inputFieldClass,
  placeholder,
  showStaricInCaseOfRequired = false,
  showErrorOnChange = false,
  showPasswordInfo = false,
  value,
  autoComplete = "",
}) => {
  const isItMobile = useCheckMobileScreen();

  const [isPasswordVisible, setPasswordVisible] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false); // Local touched state

  const handleChange = (e: any) => {
    if (!isTouched && showErrorOnChange) setIsTouched(true); // Mark as touched on first change
    onChange(e); // forward change
  };

  const showError =
    showErrorOnChange && errors[name] && (touched[name] || isTouched); // ✅ Flexible error visibility

  return (
    <div className={`form-group ${parentDivClass}`} data-testid="container">
      <label data-testid="password-label">
        {labelText}
        {showStaricInCaseOfRequired && (
          <span
            className="clr-red-dark"
            style={{ fontFamily: 'sans-serif', paddingLeft: '2px' }}
          >
            *
          </span>
        )}
      </label>
      <div className="position-relative" data-testid="password-field-container">
        <input
          name={name}
          type={isPasswordVisible ? 'text' : 'password'}
          className={`form-control pe-5 ${inputFieldClass} ${
            errors[name] && touched[name] ? 'input_error' : ''
          }`}
          onChange={handleChange}
          placeholder={placeholder}
          data-testid="password-field"
          autoComplete={autoComplete}
        />
        <span
          className="field-icon d-none d-md-inline-block"
          onClick={() => setPasswordVisible(!isPasswordVisible)}
          data-testid="icon-container"
        >
          {isPasswordVisible ? <PasswordIcons /> : <HideIcon />}
        </span>
        <span
          className="field-icon d-md-none"
          onClick={() => setPasswordVisible(!isPasswordVisible)}
          data-testid="icon-container"
        >
          {isPasswordVisible ? <PasswordIcons /> : <HideIcon />}
        </span>
      </div>

      {showPasswordInfo && !showError && !touched[name] && value?.length < 1 &&  (
        <>
        {name === 'confirm_password' ? (
          <span
            data-testid="error-span"
            className={`${
              isItMobile
                ? 'f-s12'
                : 'f-s14'
            } font-rale`}
          >
            {passwordInfoOnConfirmPassword}
          </span>
        ) : ( <ul
          style={
            isItMobile
              ? { marginLeft: '-8px' }
              : { marginLeft: '-18px', marginTop: '0.25rem' }
          }
        >
          {passowrdInfo.map((line, index) => (
            <li
              key={index}
              className={`${isItMobile ? 'f-s12' : 'f-s14'} font-rale`}
              style={{ listStyle: 'disc', fontWeight: '400' }}
            >
              {line}
            </li>
          ))}
        </ul>
      )}
      </>
      )}
      {showError &&
        (typeof errors[name] === 'string' && errors[name].includes('\n') ? (
          <ul
            style={
              isItMobile
                ? { marginLeft: '-8px' }
                : { marginLeft: '-18px', marginTop: '0.25rem' }
            }
          >
            {errors[name].split('\n').map((line, index) => (
              <li
                key={index}
                className={`clr-dark-red ${
                  isItMobile ? 'f-s12' : 'f-s14'
                } font-rale`}
                style={{ listStyle: 'disc', fontWeight: '400' }}
              >
                {line}
              </li>
            ))}
          </ul>
        ) : (
          <span className="clr-dark-red f-s14 font-rale mt-1 d-inline-block">
            {errors[name]}
          </span>
        ))}
      {!showError &&
        errors &&
        touched &&
        errors[name] &&
        touched[name] &&
        (typeof errors[name] === 'string' && errors[name].includes('\n') ? (
          <ul
            style={
              isItMobile
                ? { marginLeft: '-8px' }
                : { marginLeft: '-18px', marginTop: '0.25rem' }
            }
          >
            {errors[name].split('\n').map((line, index) => (
              <li
                key={index}
                className={`clr-dark-red ${
                  isItMobile ? 'f-s12' : 'f-s14'
                } font-rale`}
                style={{ listStyle: 'disc', fontWeight: '400' }}
              >
                {line}
              </li>
            ))}
          </ul>
        ) : (
          <span className="clr-dark-red f-s14 font-rale mt-1 d-inline-block">
            {errors[name]}
          </span>
        ))}
    </div>
  );
};

export { PasswordField };
